import React from 'react';
import { motion, useTransform, MotionValue } from 'framer-motion';
import { lerp } from '../scrollTimeline';
import { Database, Terminal, MessageSquare, GitBranch, BarChart3, BookOpen, FileTextIcon } from 'lucide-react';
interface ConnectAnimationProps {
  scrollYProgress: MotionValue<number>;
  sectionStart: number;
  sectionEnd: number;
}
const ITEMS = [{
  service: 'Slack',
  mechanism: 'MCP',
  icon: MessageSquare,
  angle: 90,
  color: '#60a5fa',
  delay: 0
}, {
  service: 'GitHub',
  mechanism: 'MCP',
  icon: GitBranch,
  angle: 30,
  color: '#f472b6',
  delay: 0.01
}, {
  service: 'Production DB',
  mechanism: 'Postgres Credentials',
  icon: Database,
  angle: 330,
  color: '#818cf8',
  delay: 0.02
}, {
  service: 'Google Analytics',
  mechanism: 'OAuth',
  icon: BarChart3,
  angle: 270,
  color: '#fb923c',
  delay: 0.03
}, {
  service: 'Notion',
  mechanism: 'MCP',
  icon: BookOpen,
  angle: 210,
  color: '#2dd4bf',
  delay: 0.015
}, {
  service: 'Core Codebase',
  mechanism: 'GitHub CLI',
  icon: Terminal,
  angle: 150,
  color: '#fbbf24',
  delay: 0.025
}, {
  icon: FileTextIcon,
  color: '#f43f5e',
  angle: 330,
  delay: 0.025
} // Rose
];
const CENTER_X = 50;
const CENTER_Y = 50;
const ORBIT_RADIUS = 45;
function getPosition(angle: number, radius: number) {
  const rad = angle * Math.PI / 180;
  return {
    x: CENTER_X + radius * Math.cos(rad),
    y: CENTER_Y - radius * Math.sin(rad)
  };
}
function ConnectItem({
  item,
  scrollYProgress,
  sectionStart,
  sectionEnd
}: {
  item: (typeof ITEMS)[number];
  scrollYProgress: MotionValue<number>;
  sectionStart: number;
  sectionEnd: number;
}) {
  const start = getPosition(item.angle, ORBIT_RADIUS);
  // Base duration is sectionEnd - sectionStart
  const duration = sectionEnd - sectionStart;
  const progressStart = sectionStart + item.delay * 0.8; // Scale delay slightly
  const progressEnd = sectionStart + duration * 0.88 + item.delay * 0.8;
  // Straight path to center — no curve offset
  const x = useTransform(scrollYProgress, [progressStart, progressEnd], [start.x, CENTER_X]);
  const y = useTransform(scrollYProgress, [progressStart, progressEnd], [start.y, CENTER_Y]);
  const opacity = useTransform(scrollYProgress, [progressStart - 0.015, progressStart, progressEnd - 0.02, progressEnd], [0, 1, 1, 0]);
  const scale = useTransform(scrollYProgress, [progressStart, progressEnd - 0.03, progressEnd], [1, 1, 0.3]);
  const Icon = item.icon;
  return <motion.div style={{
    left: useTransform(x, v => `${v}%`),
    top: useTransform(y, v => `${v}%`),
    opacity,
    scale
  }} className="absolute -translate-x-1/2 -translate-y-1/2 z-10" data-id="element-63">
      <div className="w-11 h-11 rounded-xl border backdrop-blur-sm shadow-lg flex items-center justify-center" style={{
      backgroundColor: `${item.color}15`,
      borderColor: `${item.color}30`
    }} data-id="element-64">
        <Icon className="w-5 h-5" style={{
        color: item.color
      }} data-id="element-65" />
      </div>
    </motion.div>;
}
function TrailLine({
  item,
  scrollYProgress,
  sectionStart,
  sectionEnd
}: {
  item: (typeof ITEMS)[number];
  scrollYProgress: MotionValue<number>;
  sectionStart: number;
  sectionEnd: number;
}) {
  const start = getPosition(item.angle, ORBIT_RADIUS);
  const duration = sectionEnd - sectionStart;
  const progressStart = sectionStart + item.delay * 0.8;
  const progressEnd = sectionStart + duration * 0.88 + item.delay * 0.8;
  const pathLength = useTransform(scrollYProgress, [progressStart, progressEnd], [0, 1]);
  const trailOpacity = useTransform(scrollYProgress, [progressStart - 0.015, progressStart, progressEnd, progressEnd + 0.02], [0, 0.4, 0.4, 0]);
  // Straight line to center
  const d = `M ${start.x} ${start.y} L ${CENTER_X} ${CENTER_Y}`;
  return <motion.path d={d} fill="none" stroke={item.color} strokeWidth="0.3" style={{
    pathLength,
    opacity: trailOpacity
  }} strokeLinecap="round" data-id="element-66" />;
}
export function ConnectAnimation({
  scrollYProgress,
  sectionStart,
  sectionEnd
}: ConnectAnimationProps) {
  const glowOpacity = useTransform(scrollYProgress, [sectionStart, sectionEnd], [0.1, 0.8]);
  const glowScale = useTransform(scrollYProgress, [sectionStart, sectionEnd], [0.8, 1.2]);
  const brainPulse = useTransform(scrollYProgress, [sectionStart, lerp(sectionStart, sectionEnd, 0.44), sectionEnd], [0.6, 1, 0.9]);
  // Ring starts slightly before sectionStart
  const ringStart = sectionStart - 0.02;
  const ringEnd = sectionStart + 0.02;
  const ringOpacity = useTransform(scrollYProgress, [ringStart, ringEnd], [0, 0.15]);
  return <div className="relative mx-auto" style={{
    width: '65vh',
    height: '65vh',
    maxWidth: '100%'
  }} data-id="element-67">
      {/* Central logo */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20" data-id="element-68">
        <motion.div style={{
        opacity: glowOpacity,
        scale: glowScale
      }} className="absolute -inset-12 rounded-full bg-blue-500/20 blur-3xl" data-id="element-69" />
        <motion.div style={{
        opacity: glowOpacity,
        scale: glowScale
      }} className="absolute -inset-8 rounded-full bg-purple-500/15 blur-2xl" data-id="element-70" />
        <motion.div style={{
        scale: brainPulse
      }} className="relative w-20 h-20 flex items-center justify-center" data-id="element-71">
          <img src="https://cdn.magicpatterns.com/uploads/s8vqqcPfFZev6SgcxxebLW/brainwhite.svg" alt="GigaBrain" className="w-16 h-16" data-id="element-72" />
        </motion.div>
      </div>

      {/* Trails */}
      {ITEMS.map((item, i) => <TrailLine key={`trail-${i}`} item={item} scrollYProgress={scrollYProgress} sectionStart={sectionStart} sectionEnd={sectionEnd} data-id="element-73" />)}

      {/* Items */}
      {ITEMS.map((item, i) => <ConnectItem key={`item-${i}`} item={item} scrollYProgress={scrollYProgress} sectionStart={sectionStart} sectionEnd={sectionEnd} data-id="element-74" />)}
    </div>;
}